import React, { useState } from 'react';
import axios from 'axios';
import './App.css';
import { Search, Truck, CheckCircle, AlertCircle, Clock, Package, WifiOff, MapPin, Calendar, User, Weight, FileText } from 'lucide-react';

function App() {
  const [trackingNumber, setTrackingNumber] = useState('');
  const [postcode, setPostcode] = useState('');
  const [isTracking, setIsTracking] = useState(false);
  const [trackingResult, setTrackingResult] = useState(null);
  const [error, setError] = useState('');
  const [errorType, setErrorType] = useState('validation');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setErrorType('validation');
    setTrackingResult(null);

    // Basic validation
    if (!trackingNumber.trim()) {
      setError('Voer een trackingnummer in');
      return;
    }

    if (!postcode.trim()) {
      setError('Voer een postcode in');
      return;
    }

    // Validate postcode format - simplified to work for both NL and BE
    const postcodeRegex = /^[1-9][0-9]{3}\s?[A-Z]{0,2}$/i;
    if (!postcodeRegex.test(postcode)) {
      setError('Voer een geldige postcode in (bijv. 1234 AB of 1000)');
      return;
    }

    setIsTracking(true);

    try {
      const response = await axios.get('https://staging.dutchned.trackingapi.datafirstcloud.com/api/tracking', {
        params: {
          trackingCode: trackingNumber,
          postalCode: postcode
        },
        timeout: 15000
      });

      setTrackingResult(response.data);
      console.log('UnloadingDate:', response.data.unloadingDate);
    } catch (err) {
      console.error('Error fetching tracking data:', err);
      
      if (err.code === 'ECONNABORTED') {
        setError('Het verzoek heeft te lang geduurd. Controleer uw internetverbinding en probeer het opnieuw.');
        setErrorType('network');
      } else if (err.response?.status === 404) {
        setError('Geen zending gevonden met dit trackingnummer en deze postcode');
        setErrorType('notFound');
      } else if (err.response?.status >= 500) {
        setError('Er is een serverfout opgetreden. Probeer het later opnieuw.');
        setErrorType('server');
      } else if (err.message === 'Network Error' || err.code === 'ERR_NETWORK') {
        setError('Kan geen verbinding maken met de server. Controleer uw internetverbinding en probeer het opnieuw.');
        setErrorType('network');
      } else {
        setError('Er is een onverwachte fout opgetreden. Probeer het later opnieuw.');
        setErrorType('server');
      }
    } finally {
      setIsTracking(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'in transit':
      case 'in_transit':
        return <Truck className="status-icon" />;
      case 'delivered':
      case 'afgeleverd':
        return <CheckCircle className="status-icon" />;
      case 'exception':
      case 'probleem':
        return <AlertCircle className="status-icon" />;
      case 'pending':
      case 'in afwachting':
        return <Clock className="status-icon" />;
      default:
        return <Package className="status-icon" />;
    }
  };

  const getStatusText = (status) => {
    switch (status?.toLowerCase()) {
      case 'in transit':
      case 'in_transit':
        return 'In Transit';
      case 'delivered':
      case 'afgeleverd':
        return 'Afgeleverd';
      case 'exception':
      case 'probleem':
        return 'Probleem';
      case 'pending':
      case 'in afwachting':
        return 'In Afwachting';
      default:
        return status || 'Onbekend';
    }
  };

  const renderErrorAlert = () => {
    if (!error) return null;
    
    let icon = <AlertCircle className="alert-icon" />;
    let title = 'Fout';
    let className = 'error-alert';
    
    if (errorType === 'network') {
      icon = <WifiOff className="alert-icon" />;
      title = 'Netwerkfout';
    } else if (errorType === 'notFound') {
      icon = <Package className="alert-icon" />;
      title = 'Niet gevonden';
      className = 'warning-alert';
    }
    
    return (
      <div className={`alert ${className}`}>
        {icon}
        <div className="alert-content">
          <div className="alert-title">{title}</div>
          <div className="alert-description">{error}</div>
          {errorType === 'network' && (
            <button 
              className="retry-button"
              onClick={handleSubmit}
            >
              Opnieuw proberen
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="App">
      <header className="app-header">
        <div className="container">
          <div className="logo-container">
            <img src="/logo.svg" alt="DataFirst Solutions" className="logo" />
          </div>
        </div>
      </header>

      <main>
        {!trackingResult && (
          <section className="hero-section">
            <div className="container">
              <div className="hero-content">
                <h2>Volg Uw Pakket</h2>
                <p>
                  Voer uw trackingnummer en postcode in om de huidige status en bezorgupdates te zien.
                </p>
              </div>
              <div className="tracking-form-container">
                <div className="tracking-card">
                  <div className="card-header">
                    <h3>Pakket Volgen</h3>
                    <p>Voer uw trackingnummer en postcode in</p>
                  </div>
                  <div className="card-content">
                    {renderErrorAlert()}
                    <form onSubmit={handleSubmit} className="tracking-form">
                      <div className="form-group">
                        <label htmlFor="tracking-number">Trackingnummer</label>
                        <input
                          id="tracking-number"
                          type="text"
                          placeholder="Bijv. NL9876543210"
                          value={trackingNumber}
                          onChange={(e) => setTrackingNumber(e.target.value)}
                          className="form-input"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="postcode">Postcode</label>
                        <input
                          id="postcode"
                          type="text"
                          placeholder="Bijv. 1234 AB"
                          value={postcode}
                          onChange={(e) => setPostcode(e.target.value)}
                          className="form-input"
                        />
                      </div>
                      <button
                        type="submit"
                        className="submit-button"
                        disabled={isTracking}
                      >
                        {isTracking ? (
                          <>
                            <span className="spinner"></span>
                            Zoeken...
                          </>
                        ) : (
                          <>
                            <Search className="button-icon" />
                            Pakket Zoeken
                          </>
                        )}
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {trackingResult && (
          <section className="results-section">
            <div className="container">
              <div className="search-form-compact">
                <form onSubmit={handleSubmit} className="compact-form">
                  <input
                    type="text"
                    placeholder="Trackingnummer"
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                    className="compact-input"
                  />
                  <input
                    type="text"
                    placeholder="Postcode"
                    value={postcode}
                    onChange={(e) => setPostcode(e.target.value)}
                    className="compact-input"
                  />
                  <button
                    type="submit"
                    className="compact-button"
                    disabled={isTracking}
                  >
                    {isTracking ? (
                      <span className="spinner"></span>
                    ) : (
                      <Search className="button-icon" />
                    )}
                  </button>
                </form>
              </div>
              <div className="results-card large">
                <div className="card-header results-header">
                  <div className="results-title">
                    <h3>Tracking Details</h3>
                    <p>Trackingnummer: {trackingNumber}</p>
                    {trackingResult.unloadingDate && trackingResult.unloadingDate !== 'N/A' && (
                      <p>Bezorgdatum: {trackingResult.unloadingDate}</p>
                    )}
                  </div>
                  <div className="status-badge">
                    {getStatusIcon(trackingResult.status)}
                    <span className="status-text">{getStatusText(trackingResult.status)}</span>
                  </div>
                </div>
                <div className="card-content">
                  <div className="shipment-details">
                    <div className="detail-group">
                      <h4>Verzendgegevens</h4>
                      <div className="detail-list">
                        <div className="detail-item">
                          <FileText className="detail-icon" />
                          <span><strong>Referentie:</strong> {trackingResult.reference || 'N/A'}</span>
                        </div>
                        <div className="detail-item">
                          <Weight className="detail-icon" />
                          <span><strong>Gewicht:</strong> {trackingResult.weight || 0} kg</span>
                        </div>
                        <div className="detail-item">
                          <Package className="detail-icon" />
                          <span><strong>Beschrijving:</strong> {trackingResult.description || 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                    <div className="detail-group">
                      <h4>Bezorggegevens</h4>
                      <div className="detail-list">
                        <div className="detail-item">
                          <User className="detail-icon" />
                          <span><strong>Van:</strong> {trackingResult.sender || 'N/A'}</span>
                        </div>
                        <div className="detail-item">
                          <User className="detail-icon" />
                          <span><strong>Aan:</strong> {trackingResult.recipient || 'N/A'}</span>
                        </div>
                        <div className="detail-item">
                          <MapPin className="detail-icon" />
                          <span><strong>Postcode:</strong> {trackingResult.postalCode || 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="tracking-events">
                    <h4>Verzendvoortgang</h4>
                    <div className="events-list">
                      {trackingResult.trackingEvents?.map((event, index) => (
                        <div key={index} className="event-item">
                          <div className="event-date">
                            <Calendar className="event-date-icon" />
                            <div>{event.date}</div>
                          </div>
                          <div className="event-content">
                            <div className="event-status">{event.status}</div>
                            <div className="event-location">
                              <MapPin className="event-location-icon" />
                              {event.location}
                            </div>
                          </div>
                        </div>
                      )) || (
                        <div className="no-events">
                          <Package className="no-events-icon" />
                          <p>Geen tracking events beschikbaar</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}
      </main>

      <footer className="app-footer">
        <div className="container">
          <p>&copy; 2025 DataFirst Solutions. Alle rechten voorbehouden.</p>
        </div>
      </footer>
    </div>
  );
}

export default App;
